<template>
  <CommunitiesRowSkeleton
    v-if="loading"
    :show-profile-avatar="Boolean(!largeDesktop && userStore.isAuthenticated)"
    :show-plus-button="userStore.isAuthenticated"
    :count="sortedChannelCommunities.length || 3"
    v-bind="$attrs"
  />

  <div
    v-else
    class="flex flex-row flex-nowrap overflow-x-auto gap-2"
    v-bind="$attrs"
  >
    <RouterLink
      v-if="!largeDesktop && userStore.user"
      :class="
        layoutClass(
          newLayout,
          'p-5u ring-inset ring-brand-primary ring-[3px] border-[4px] border-brand-quaternary-40 rounded-full hover:ring-8 hover:border-brand-tertiary-60 active:border-brand-secondary-80 transition-all duration-150 flex',
          'p-5u ring-inset ring-fill-brand ring-[3px] border-[4px] border-fill-brand-secondary rounded-full hover:ring-8 hover:border-fill-brand-pressed active:border-fill-brand-hover transition-all duration-150 flex',
        )
      "
      :to="{
        name: RouteNames.Profile,
        params: { channel: channelStore.channelSlug },
      }"
    >
      <Avatar
        :avatar="{
          name: userStore.username,
          image: userStore.user.avatar,
          id: userStore.user.id,
        }"
        size="md"
      />
    </RouterLink>

    <IntercomButton
      v-if="!largeDesktop && !appStore.isSimulfest"
      variant="primary"
    />

    <ButtonV2
      v-if="userStore.isAuthenticated"
      variant="subtle"
      size="xs"
      class="!rounded-full size-50u !p-0"
      @click="isCommunitiesSelectorVisible = true"
    >
      <div
        class="flex-1 h-full items-center justify-center flex rounded-full transition-all duration-200"
      >
        <Icon icon="mdi:plus" class="text-text size-6" />
      </div>
    </ButtonV2>

    <ButtonV2
      v-if="channelStore.channel"
      variant="subtle"
      size="xs"
      class="!rounded-full size-50u !p-0"
      @click="channelStore.resetCommunitySelection"
    >
      <div
        class="flex-1 h-full items-center justify-center flex rounded-full transition-all duration-200 relative"
        :class="{
          'ring-inset ring-fill-brand ring-4':
            themeStore.currentTheme?.id === channelStore.channelSlug,
        }"
      >
        <Avatar
          :avatar="{
            name: channelStore.channel.name,
            image: channelStore.channel.logo ?? undefined,
            id: channelStore.channel.id,
          }"
        />
      </div>
    </ButtonV2>

    <template v-if="sortedChannelCommunities.length">
      <ButtonV2
        v-for="community in sortedChannelCommunities"
        :key="community.id"
        variant="subtle"
        size="xs"
        class="!rounded-full size-50u !p-0"
        @click="themeStore.toggleSelectedCommunityTheme(community.id)"
      >
        <div
          class="flex-1 h-full items-center justify-center flex rounded-full transition-all duration-200 relative"
          :class="{
            'ring-inset ring-fill-brand ring-4':
              themeStore.currentTheme?.id === community.id,
          }"
        >
          <Spinner
            v-if="themeStore.loadingThemeDataMap.get(community.id)"
            class="rounded-full"
          />
          <Avatar
            v-else
            :avatar="{
              name: community.name,
              image: community.image ?? undefined,
              id: community.id,
            }"
          />
        </div>
      </ButtonV2>
    </template>

    <template v-else>
      <p class="self-center text-start">
        {{ $t("channel.communities.actions.select") }}
      </p>
    </template>
  </div>

  <Teleport :to="portalRootElementSelector">
    <ModalRoot v-model="isCommunitiesSelectorVisible">
      <template #body="{ close }">
        <ModalBodyV2 @close="close">
          <template #title>
            <h2 class="text-3xl">
              {{ $t("channel.communities.selector.title") }}
            </h2>
          </template>

          <UserCommunitySelector
            v-slot="{
              communities,
              selectedCommunitiesIds,
              isSubmitting,
              onSubmit,
            }"
            @finish="onUpdate"
          >
            <CommunityFollowsSelector
              :communities="communities"
              :selected-communities-ids="selectedCommunitiesIds"
              :is-submitting="isSubmitting"
              @submit="onSubmit"
            >
              <template #submit="props">
                <slot name="submit" v-bind="props"></slot>
              </template>
              <template #title>{{
                $t("channel.communities.selector.title")
              }}</template>
            </CommunityFollowsSelector>
          </UserCommunitySelector>
        </ModalBodyV2>
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import ModalRoot from "../ui/modal/modal-root.vue";
import { Icon } from "@iconify/vue";
import { apolloClient } from "@/utils/apollo";
import UserCommunitySelector from "./user-community-selector.vue";
import ButtonV2 from "../ui/button/button-v2.vue";
import Avatar from "../me/avatar.vue";
import { useThemeStore } from "@/stores/theme.store";
import ModalBodyV2 from "../ui/modal/modal-body-v2.vue";
import CommunityFollowsSelector from "./community-follows-selector.vue";
import Spinner from "../ui/spinner.vue";
import type { Community } from "@/generated/graphql";
import { portalRootElementSelector } from "@/constants/ui";

import { RouterLink } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useUserStore } from "@/stores/user.store";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import CommunitiesRowSkeleton from "@/components/ui/skeleton/communities-row-skeleton.vue";
import IntercomButton from "./intercom-button.vue";
import { useAppStore } from "@/stores/app.store";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import { layoutClass } from "@/utils/layout-class";

defineOptions({
  inheritAttrs: false,
});

const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

const channelStore = useChannelStore();
const themeStore = useThemeStore();

const appStore = useAppStore();
const userStore = useUserStore();
const isCommunitiesSelectorVisible = ref(false);
const { largeDesktop } = useResponsiveHelpers();

const loading = computed(
  () => channelStore.loadingChannel || !channelStore.channel,
);

const sortedChannelCommunities = computed(() => {
  const list: Community[] = [];

  const followedCommunitiesMap: Record<string, Community> = {};

  for (const community of channelStore.userCommunities) {
    list.push(community);
    followedCommunitiesMap[community.id] = community;
  }

  for (const community of channelStore.channelCommunities) {
    if (!followedCommunitiesMap[community.id]) {
      list.push(community);
    }
  }

  return list;
});

async function onUpdate() {
  isCommunitiesSelectorVisible.value = false;
  // TODO: Organize queries and only revalidate the channel and user communities

  await apolloClient.refetchQueries({
    include: "all",
  });
  channelStore.refreshUserInChannel();
}
</script>

<style scoped>
.communities-mask {
  mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 1) calc(100% - 2rem),
    rgba(0, 0, 0, 0) 100%
  );
}
</style>
